import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { inputCls, inputClsError, Btn } from "@/dashboard/ui";
import PhoneInput, { phoneIsValid } from "@/components/PhoneInput";
import { CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import VoiceStudio from "@/components/VoiceStudio";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function Soundwaves() {
  const bars = Array.from({ length: 48 }, (_, i) => i);
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-[6px] overflow-hidden opacity-[0.16]" aria-hidden="true">
      {bars.map((i) => (
        <motion.span
          key={i}
          className="w-[3px] rounded-full bg-[#111111]"
          animate={{ height: [14, 14 + Math.abs(Math.sin(i * 0.7)) * 90, 14] }}
          transition={{ duration: 2.4 + (i % 5) * 0.35, repeat: Infinity, ease: "easeInOut", delay: i * 0.06 }}
        />
      ))}
    </div>
  );
}

export default function EnquiryForm() {
  const reduce = useReducedMotion();
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", message: "", consent: false });
  const [cbMode, setCbMode] = useState("now");
  const [cbWhen, setCbWhen] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [doneInfo, setDoneInfo] = useState(null);
  const [errs, setErrs] = useState({});

  const setField = (key, v) => {
    setForm((f) => ({ ...f, [key]: v }));
    // clear the error as soon as the field becomes valid
    setErrs((prev) => {
      if (!prev[key]) return prev;
      const valid =
        key === "full_name" ? v.trim().length > 0
        : key === "email" ? EMAIL_RE.test(v.trim())
        : key === "phone" ? phoneIsValid(v)
        : key === "consent" ? v === true
        : false;
      if (!valid) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    const next = {};
    if (!form.full_name.trim()) next.full_name = "Please enter your full name.";
    if (!form.phone.trim()) next.phone = "Please enter your phone number.";
    else if (!phoneIsValid(form.phone)) next.phone = "Please enter a valid phone number.";
    if (!form.email.trim()) next.email = "Please enter your email address.";
    else if (!EMAIL_RE.test(form.email.trim())) next.email = "Please enter a valid email address.";
    if (!form.consent) next.consent = "Please agree to receive an AI call from Savy Kerno.";
    if (cbMode === "scheduled") {
      if (!cbWhen) next.scheduled_at = "Please choose a date and time.";
      else if (new Date(cbWhen) <= new Date()) next.scheduled_at = "Please choose a future time.";
    }
    setErrs(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      const payload = {
        full_name: form.full_name.trim(), phone: form.phone, email: form.email.trim(),
        message: form.message || null, mode: cbMode, consent: form.consent,
        timezone: tz, ...(cbMode === "scheduled" ? { scheduled_at: cbWhen } : {}),
      };
      const { data } = await api.post("/public/callback", payload);
      setDoneInfo(data);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="enquiry-section"
      className="relative overflow-hidden border-t border-neutral-100 py-20 sm:py-28"
      style={{ background: "linear-gradient(180deg, #ffffff 0%, #f2f2f3 55%, #e9e9eb 100%)" }}
    >
      {!reduce && <Soundwaves />}
      <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">Get in touch</p>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-[#111111] sm:text-5xl">
            See what AI answering feels like
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-neutral-600 sm:text-lg">
            Two ways to experience it — talk to a demo agent in your browser, or leave your
            details and we'll call you back.
          </p>

          <div className="mt-8" data-testid="voice-demo-embed">
            <VoiceStudio compact />
          </div>

          <ul className="mt-6 space-y-2.5 text-sm text-neutral-600">
            <li>· We only use your details to respond to this enquiry</li>
            <li>· No newsletters, no spam, no obligation</li>
          </ul>
        </div>

        <div className="rounded-3xl border border-white/60 bg-white/90 p-7 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.18)] backdrop-blur sm:p-9">
          <p className="mb-1 text-sm font-bold text-[#111111]">Request an AI Call Back</p>
          <p className="mb-5 text-xs leading-relaxed text-neutral-500">
            Ask Savy Kerno's AI assistant to call you. Outbound calling isn't connected yet — your
            request is saved securely and we'll enable it once our calling provider is verified.
          </p>
          {done ? (
            <div className="flex flex-col items-center py-8 text-center" data-testid="enquiry-success">
              <CheckCircle2 size={40} className="text-[#111111]" />
              <p className="mt-4 text-lg font-bold text-[#111111]">Request received</p>
              <p className="mt-2 max-w-xs text-sm text-neutral-500">
                {doneInfo?.status === "pending_provider"
                  ? "Your AI callback is saved as pending — our calling provider isn't connected yet, so no call has been placed. We'll contact you once it's available."
                  : "Your request has been saved."}
              </p>
              {doneInfo?.scheduled_at && (
                <p className="mt-2 rounded-xl bg-neutral-50 px-4 py-2 text-xs font-semibold text-neutral-600" data-testid="enquiry-scheduled-info">
                  Requested for {new Date(doneInfo.scheduled_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })} ({doneInfo.timezone || "your timezone"})
                </p>
              )}
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="space-y-4" data-testid="enquiry-form">
              <div className="grid grid-cols-2 gap-2" data-testid="callback-mode-picker">
                <button
                  type="button"
                  onClick={() => setCbMode("now")}
                  aria-pressed={cbMode === "now"}
                  data-testid="callback-mode-now"
                  className={`rounded-xl border px-3 py-2.5 text-left text-xs font-semibold transition-colors ${cbMode === "now" ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600 hover:border-neutral-400"}`}
                >
                  Call Me Now
                  <span className={`mt-0.5 block text-[10px] font-normal ${cbMode === "now" ? "text-neutral-300" : "text-neutral-400"}`}>As soon as calling is live</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCbMode("scheduled")}
                  aria-pressed={cbMode === "scheduled"}
                  data-testid="callback-mode-scheduled"
                  className={`rounded-xl border px-3 py-2.5 text-left text-xs font-semibold transition-colors ${cbMode === "scheduled" ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600 hover:border-neutral-400"}`}
                >
                  Schedule an AI Call
                  <span className={`mt-0.5 block text-[10px] font-normal ${cbMode === "scheduled" ? "text-neutral-300" : "text-neutral-400"}`}>Pick a date & time</span>
                </button>
              </div>
              {cbMode === "scheduled" && (
                <div>
                  <label htmlFor="callback-when" className="mb-1.5 block text-xs font-semibold text-neutral-600">Date & time *</label>
                  <input
                    id="callback-when"
                    data-testid="callback-when"
                    type="datetime-local"
                    className={errs.scheduled_at ? inputClsError : inputCls}
                    value={cbWhen}
                    onChange={(e) => setCbWhen(e.target.value)}
                    min={new Date(Date.now() + 3600e3).toISOString().slice(0, 16)}
                    aria-invalid={!!errs.scheduled_at || undefined}
                  />
                  <p className="mt-1 text-[10px] text-neutral-400">Your local time — we'll honour your timezone.</p>
                  {errs.scheduled_at && <p role="alert" data-testid="callback-when-error" className="mt-1.5 text-xs text-red-600">{errs.scheduled_at}</p>}
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="enquiry-name" className="mb-1.5 block text-xs font-semibold text-neutral-600">Full name *</label>
                  <input
                    id="enquiry-name"
                    data-testid="enquiry-name-input"
                    className={errs.full_name ? inputClsError : inputCls}
                    value={form.full_name}
                    onChange={(e) => setField("full_name", e.target.value)}
                    aria-invalid={!!errs.full_name || undefined}
                    autoComplete="name"
                  />
                  {errs.full_name && <p role="alert" data-testid="enquiry-name-error" className="mt-1.5 text-xs text-red-600">{errs.full_name}</p>}
                </div>
                <PhoneInput
                  testid="enquiry-phone-input"
                  label="Phone"
                  required
                  value={form.phone}
                  invalid={!!errs.phone}
                  error={errs.phone}
                  onChange={(v) => setField("phone", v)}
                />
              </div>
              <div>
                <label htmlFor="enquiry-email" className="mb-1.5 block text-xs font-semibold text-neutral-600">Email *</label>
                <input
                  id="enquiry-email"
                  data-testid="enquiry-email-input"
                  type="email"
                  className={errs.email ? inputClsError : inputCls}
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  aria-invalid={!!errs.email || undefined}
                  autoComplete="email"
                />
                {errs.email && <p role="alert" data-testid="enquiry-email-error" className="mt-1.5 text-xs text-red-600">{errs.email}</p>}
              </div>
              <div>
                <label htmlFor="enquiry-message" className="mb-1.5 block text-xs font-semibold text-neutral-600">How can we help?</label>
                <textarea id="enquiry-message" data-testid="enquiry-message-input" rows={3} className={inputCls} value={form.message} onChange={(e) => setField("message", e.target.value)} placeholder="Tell us about your business…" />
              </div>
              <div>
                <label className={`flex items-start gap-2.5 rounded-xl border p-3 text-xs leading-relaxed text-neutral-600 ${errs.consent ? "border-red-500" : "border-transparent"}`}>
                  <input
                    data-testid="enquiry-consent-checkbox"
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => setField("consent", e.target.checked)}
                    aria-invalid={!!errs.consent || undefined}
                    className="mt-0.5 h-4 w-4 rounded accent-[#111111]"
                  />
                  I agree to receive an AI phone call from Savy Kerno's AI assistant about my enquiry.
                </label>
                {errs.consent && <p role="alert" data-testid="enquiry-consent-error" className="mt-1.5 text-xs text-red-600">{errs.consent}</p>}
              </div>
              {error && (
                <p data-testid="enquiry-error" role="alert" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>
              )}
              <Btn data-testid="enquiry-submit-button" type="submit" disabled={busy} className="w-full py-3.5">
                {busy ? "Sending…" : cbMode === "now" ? "Request AI Call Back" : "Schedule AI Call"}
              </Btn>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
